import { BottomRow, NavigationButton, Preview, PreviewMidRow } from 'components/Main/table/TableStyles';
import { DarkButton } from 'components/style/Global';
import React, { useEffect, useState, useRef } from 'react';
import { GoChevronLeft } from 'react-icons/go';
import DataTable from './DataTable';
import { useCampaignContext } from 'context/CampaignContext';
import ChartsContainer from './ChartsContainer';
import {
  overwriteItemResponse,
  StoreQueriesMutation,
  StoreQueriesV2Mutation,
  UserQuery,
  BenchmarkingIndexValues,
} from 'API';
import { API } from 'aws-amplify';
import { storeQueries, storeQueriesV2 } from 'graphql/mutations';
import { set } from 'lodash';
import { useGetMainTotalQuery } from 'customHooks/useGetMainTotalQuery';

import TVChartsContainer from './TV/TVChartsContainer';
import RadioChartsContainer from './RADIO/RadioChartsContainer';
import OOHChartsContainer2 from './OOH&PRINT/OOHContainer';
import { ErrorBoundary } from 'react-error-boundary';
import { placeholderFullSources, PlaceholderType } from './PlaceholderContainer';
import CustomReportChartsContainer from 'components/NewPortal/CustomReports/CustomReportChartsContainer';
import { useGetCCTotalQuery } from 'customHooks/useGetCCTotalQuery.';

type Props = {
  isGraph: boolean;
  joinedData: any[] | undefined;
  title?: string;
  setTitle(title: string): void;
  page: number;
  ccData: any;
  userMainQuery: any;
  userCCQuery: any;
  nextPage(): any;
  previousPage(): any;
  chartMetrics: any;
  componentID: string;
  setChartMetrics(chartMetric: any): any;
  mainQueryID: string;
  ccQueryID: string;
  benchmarkQueryID?: string;
  element: PlaceholderType | undefined;
  placeholderList?: PlaceholderType[] | undefined;
  setTablePlaceholderList?(value: React.SetStateAction<PlaceholderType[]>): void;
  setStoredQueries?: React.Dispatch<React.SetStateAction<UserQuery[]>>;
  queryError: string;
  setUserMainQuery(query: any): any;
  setUserCCQuery(query: any): any;
  setUserCCTotalQuery(query: any): any;
  setUserMainQueryTotal(query: any): any;
  setPage(page: number): any;
  setNewQuery(newQuery: 'OLD' | 'NEW' | 'BACK' | 'APPLY'): any;
  userBenchmarkQuery: any;
  setUserBenchmarkQuery(query: any): any;
  setUserBenchmarkTotalQuery(query: any): any;
  selectedDataSource: placeholderFullSources | undefined;
  savePlaceholderToOrder2?: (componentId: string) => void;
  updateTablePlaceholderDuplicate?: (
    queryId: string,
    id: string,
    isDupicated: boolean | undefined,
    newOriginalId?: string | undefined,
  ) => void;
  defaultSelected?: boolean;
  latestOverwrittenData?: overwriteItemResponse[] | undefined;
  saveCustomReport?: () => void;
  modalType?: string;
  benchmarkIndex?: BenchmarkingIndexValues | null;
  setBenchmarkIndex?: (value: BenchmarkingIndexValues | null) => void;
  setQueryError?: (error: string) => void;
  hideTotal: boolean;
  setHideTotal: (value: boolean) => void;
};

const PreviewContainer = (props: Props) => {
  const {
    isGraph,
    joinedData,
    title,
    setTitle,
    page,
    ccData,
    userCCQuery,
    userMainQuery,
    nextPage,
    previousPage,
    chartMetrics,
    setChartMetrics,
    componentID,
    mainQueryID,
    element,
    ccQueryID,
    benchmarkQueryID,
    setUserMainQuery,
    setUserCCQuery,
    setUserCCTotalQuery,
    setUserMainQueryTotal,
    queryError,
    setPage,
    savePlaceholderToOrder2,
    updateTablePlaceholderDuplicate,
    latestOverwrittenData,
    saveCustomReport,
    modalType,
    selectedDataSource,
    benchmarkIndex,
    setBenchmarkIndex,
    setQueryError,
    hideTotal,
    setHideTotal,
  } = props;
  const { dashType, activeSubCampaign, activeCampaign } = useCampaignContext();

  const [isDifferent, setIsDifferent] = useState(false);

  // Track hideTotal changes to trigger APPLY button
  const initialHideTotalRef = useRef<boolean>(hideTotal);

  // SECOND QUERY AND ITS TOTAL
  const [secondMainQuery, setSecondMainQuery] = useState<any>();
  const [mainQueryTotal] = useGetMainTotalQuery(secondMainQuery, 'MAIN', hideTotal || false);
  // SECOND QUERY CC AND  ITS TOTAL
  const [secondCCQuery, setSecondCCQuery] = useState<any>();
  const [ccQueryTotal] = useGetCCTotalQuery(secondCCQuery);

  // SECOND BENCHMARK QUERY
  const [secondBenchmarkQuery, setSecondBenchmarkQuery] = useState<any>();
  const [secondBenchmarkTotalQuery] = useGetMainTotalQuery(secondBenchmarkQuery, 'BENCHMARK', hideTotal || false);

  // useEffect for hideTotal changes - must be AFTER state declarations
  useEffect(() => {
    // If hideTotal changed from initial value, show APPLY button
    if (hideTotal !== initialHideTotalRef.current) {
      setIsDifferent(true);
      // Only set second queries if they haven't been set yet (no other changes pending)
      // This prevents overwriting user's other pending changes
      if (!secondMainQuery && userMainQuery) {
        setSecondMainQuery(JSON.parse(JSON.stringify(userMainQuery)));
      }
      if (!secondCCQuery && userCCQuery) {
        setSecondCCQuery(JSON.parse(JSON.stringify(userCCQuery)));
      }
      if (!secondBenchmarkQuery && props.userBenchmarkQuery) {
        setSecondBenchmarkQuery(JSON.parse(JSON.stringify(props.userBenchmarkQuery)));
      }
    }
  }, [hideTotal]);

  // Reset isDifferent when parent queries change (after APPLY is processed)
  useEffect(() => {
    if (isDifferent) {
      // Don't reset if hideTotal has changed from initial value
      const hideTotalChanged = hideTotal !== initialHideTotalRef.current;
      if (hideTotalChanged) {
        return;
      }

      // Check if the "second" queries now match the current queries
      const mainMatches = secondMainQuery
        ? JSON.stringify(secondMainQuery) === JSON.stringify(userMainQuery)
        : !userMainQuery;
      const ccMatches = secondCCQuery ? JSON.stringify(secondCCQuery) === JSON.stringify(userCCQuery) : !userCCQuery;
      const benchmarkMatches = secondBenchmarkQuery
        ? JSON.stringify(secondBenchmarkQuery) === JSON.stringify(props.userBenchmarkQuery)
        : !props.userBenchmarkQuery;

      if (mainMatches && ccMatches && benchmarkMatches) {
        setIsDifferent(false);
      }
    }
  }, [
    userMainQuery,
    userCCQuery,
    props.userBenchmarkQuery,
    isDifferent,
    secondMainQuery,
    secondCCQuery,
    secondBenchmarkQuery,
  ]);

  const handleChanges = (secondQuery: any, secondQueryConversions: any, benchmarkQuery?: any) => {
    const mainQueryChanged =
      secondQuery && userMainQuery
        ? JSON.stringify(secondQuery) !== JSON.stringify(userMainQuery)
        : Boolean(secondQuery) !== Boolean(userMainQuery);

    const ccQueryChanged =
      secondQueryConversions && userCCQuery
        ? JSON.stringify(secondQueryConversions) !== JSON.stringify(userCCQuery)
        : Boolean(secondQueryConversions) !== Boolean(userCCQuery);

    const benchmarkQueryChanged =
      benchmarkQuery && props.userBenchmarkQuery
        ? JSON.stringify(benchmarkQuery) !== JSON.stringify(props.userBenchmarkQuery)
        : Boolean(benchmarkQuery) !== Boolean(props.userBenchmarkQuery);
    console.log('BENCH QUERY CHANGED', benchmarkQueryChanged, benchmarkQuery, props.userBenchmarkQuery);

    if (mainQueryChanged || ccQueryChanged || benchmarkQueryChanged) {
      setIsDifferent(true);
      setSecondMainQuery(secondQuery);
      delete secondQueryConversions?.query.groupFilters; // Ensure groupFilters are removed
      setSecondCCQuery(secondQueryConversions);
      delete benchmarkQuery?.query.groupFilters; // Ensure groupFilters are removed
      setSecondBenchmarkQuery(benchmarkQuery);
    } else {
      console.log('SAME');
    }
  };

  const joinedDataCopy = joinedData && JSON.parse(JSON.stringify(joinedData));
  const mostkeys =
    joinedDataCopy && joinedDataCopy.sort((a: any, b: any) => Object.keys(b).length - Object.keys(a).length)[0];
  const dimensionCount = [];
  if (mostkeys) {
    for (const [key, value] of Object.entries(mostkeys)) {
      if (
        typeof value === 'string' &&
        value !== 'Infinity' &&
        key !== 'Start date' &&
        key !== 'End date' &&
        value !== 'NaN'
      ) {
        dimensionCount.push(value);
      }
    }
  }

  const saveDisabled = isGraph && props.saveCustomReport && dimensionCount.length > 1;

  const handleSave = async () => {
    if (props.saveCustomReport) {
      props.saveCustomReport();
      return;
    }
    await saveQuery();
    async function saveQuery() {
      try {
        // const correctComponentID = element?.original_id ? element.original_id : componentID;
        const correctComponentID = componentID;

        const varNoConversions = {
          campaign_id:
            /* activeSubCampaign?.name
            ? activeSubCampaign.subcampaignId?.toString()
            : */ activeCampaign?.campaignId?.toString(),
          position: correctComponentID,
          query_id: mainQueryID,
          ...(benchmarkQueryID && { query_id_benchmark: benchmarkQueryID }),
          ...(benchmarkIndex && { benchmarkingIndex: benchmarkIndex }),
          hideTotal: hideTotal && userMainQuery?.query?.groupFilters?.length > 0 ? true : false,
          title: props.title,
          metrics: props.chartMetrics.map((m: any) => {
            return {
              chartType: m.chartType,
              metric: m.metric,
              axis: m.axis,
            };
          }),
        };

        const varWithConversions = {
          campaign_id: activeSubCampaign?.name
            ? activeSubCampaign.subcampaignId?.toString()
            : activeCampaign?.campaignId?.toString(),
          position: correctComponentID,
          query_id: mainQueryID,
          query_id_cc: ccQueryID,
          ...(benchmarkQueryID && { query_id_benchmark: benchmarkQueryID }),
          ...(benchmarkIndex && { benchmarkingIndex: benchmarkIndex }),
          hideTotal: hideTotal && userMainQuery?.query?.groupFilters?.length > 0 ? true : false,
          title: props.title,
          metrics: props.chartMetrics.map((m: any) => {
            return {
              chartType: m.chartType,
              metric: m.metric,
              axis: m.axis,
            };
          }),
        };

        const saveResponse = (await API.graphql({
          query: storeQueriesV2,
          variables: ccQueryID !== '' && ccData ? varWithConversions : varNoConversions,
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as { data: StoreQueriesV2Mutation };

        savePlaceholderToOrder2 && savePlaceholderToOrder2(correctComponentID);
        updateTablePlaceholderDuplicate &&
          updateTablePlaceholderDuplicate(mainQueryID, correctComponentID, element?.duplicated);
        // element?.duplicated === true &&

        setPage(7);

        nextPage();
      } catch (e) {
        console.error('ERROR SAVING QUERY', e);
      }
    }
  };
  return (
    <Preview displayPreview={6}>
      <PreviewMidRow
        style={{ justifyContent: queryError !== '' ? 'left' : 'center' }}
        className={saveCustomReport ? 'customReport' : ''}
      >
        {queryError !== '' ? (
          <div style={{ paddingTop: queryError !== '' ? '1.5vw' : 'normal' }}>
            <h1 style={{ fontSize: '1.2vw', paddingBottom: '.4vw' }}>
              {queryError} <br />
            </h1>
            <p>
              {queryError.includes('was detected as the data source')
                ? 'Please select the correct data source from the list or fix the issue in Campaign Mapping.'
                : ' Please click Start New Query to redefine your query, otherwise this placeholder will be ignored'}
            </p>
          </div>
        ) : (
          <>
            {joinedData && !isGraph && (
              <DataTable
                data={joinedData}
                title={title}
                setTitle={setTitle}
                page={page}
                userMainQuery={userMainQuery}
                userCCQuery={userCCQuery}
                userBenchmarkQuery={props.userBenchmarkQuery}
                selectedDataSource={selectedDataSource}
                conversionsData={ccData}
                handleChanges={handleChanges}
                latestOverwrittenData={latestOverwrittenData}
                saveCustomReport={saveCustomReport}
                benchmarkIndex={benchmarkIndex}
                setBenchmarkIndex={setBenchmarkIndex}
                hideTotal={hideTotal}
                setHideTotal={setHideTotal}
                // componentID={element?.id.startsWith('BVOD') ? element?.id : undefined}
              ></DataTable>
            )}

            {joinedData &&
              isGraph &&
              (dashType === 'DIGITAL' ||
                dashType === 'SEARCH' ||
                dashType === 'INSOURCE_DATA' ||
                (dashType === 'TV' && element?.id.startsWith('BVOD'))) && (
                <ErrorBoundary fallback={<div>Something went wrong</div>}>
                  <ChartsContainer
                    data={joinedData}
                    title={title}
                    setTitle={setTitle}
                    page={page}
                    currentQuery={userMainQuery}
                    currentConversionsQuery={userCCQuery}
                    conversionsData={ccData}
                    chartMetrics={chartMetrics}
                    setChartMetrics={(chartMetric) => setChartMetrics(chartMetric)}
                    handleChanges={handleChanges}
                    componentID={componentID}
                    latestOverwrittenData={latestOverwrittenData}
                    hideTotal={hideTotal}
                    setHideTotal={setHideTotal}
                  ></ChartsContainer>
                </ErrorBoundary>
              )}

            {joinedData && isGraph && dashType === 'FLOWCHART' && (
              <ErrorBoundary fallback={<div>Something went wrong</div>}>
                <CustomReportChartsContainer
                  data={joinedData}
                  title={title}
                  setTitle={setTitle}
                  page={page}
                  currentQuery={userMainQuery}
                  currentConversionsQuery={userCCQuery}
                  conversionsData={ccData}
                  chartMetrics={chartMetrics}
                  setChartMetrics={(chartMetric) => setChartMetrics(chartMetric)}
                  handleChanges={handleChanges}
                  componentID={componentID}
                  latestOverwrittenData={latestOverwrittenData}
                  modalType={modalType}
                  benchmarkIndex={benchmarkIndex}
                  setBenchmarkIndex={setBenchmarkIndex}
                  hideTotal={hideTotal}
                  setHideTotal={setHideTotal}
                ></CustomReportChartsContainer>
              </ErrorBoundary>
            )}

            {
              <>
                {joinedData && isGraph && dashType === 'TV' && (
                  <ErrorBoundary fallback={<div>Something went wrong</div>}>
                    <TVChartsContainer
                      data={joinedData}
                      componentID={componentID}
                      page={page}
                      title={title}
                      setTitle={(title) => props.setTitle(title)}
                      chartMetrics={props.chartMetrics}
                      setChartMetrics={(chartMetric) => props.setChartMetrics(chartMetric)}
                      currentQuery={userMainQuery}
                      currentConversionsQuery={userCCQuery}
                      conversionsData={ccData}
                      handleChanges={handleChanges}
                    ></TVChartsContainer>
                  </ErrorBoundary>
                )}

                {joinedData && isGraph && dashType === 'RADIO' && (
                  <ErrorBoundary fallback={<div>Something went wrong</div>}>
                    <RadioChartsContainer
                      data={joinedData}
                      componentID={componentID}
                      page={page}
                      title={title}
                      setTitle={(title) => props.setTitle(title)}
                      chartMetrics={props.chartMetrics}
                      setChartMetrics={(chartMetric) => props.setChartMetrics(chartMetric)}
                      currentQuery={userMainQuery}
                      currentConversionsQuery={userCCQuery}
                      conversionsData={ccData}
                      handleChanges={handleChanges}
                    ></RadioChartsContainer>
                  </ErrorBoundary>
                )}

                {joinedData && isGraph && (dashType === 'OOH' || dashType === 'PRINT' || dashType === 'CINEMA') && (
                  <ErrorBoundary fallback={<div>Something went wrong</div>}>
                    <OOHChartsContainer2
                      data={joinedData}
                      componentID={props.componentID}
                      page={props.page}
                      title={props.title}
                      setTitle={(title) => props.setTitle(title)}
                      chartMetrics={props.chartMetrics}
                      setChartMetrics={(chartMetric) => props.setChartMetrics(chartMetric)}
                      currentQuery={userMainQuery}
                      currentConversionsQuery={userCCQuery}
                      conversionsData={ccData}
                      // handleChanges={handleChanges}
                    ></OOHChartsContainer2>
                  </ErrorBoundary>
                )}
              </>
            }
          </>
        )}
      </PreviewMidRow>

      {joinedData && props.page === 6 ? (
        <BottomRow isGraph={props.isGraph}>
          {queryError === '' ? (
            <NavigationButton
              type="button"
              style={{ backgroundColor: 'transparent' }}
              /* onClick={props.handleBackButton} */ onClick={() => {
                /* props.setNewQuery("BACK"); */ previousPage();
              }}
            >
              <GoChevronLeft /> Back
            </NavigationButton>
          ) : (
            <NavigationButton
              type="button"
              style={{ backgroundColor: 'transparent' }}
              onClick={() => {
                setQueryError && setQueryError('');
                // Reset queries and start fresh
                setUserMainQuery(undefined);
                setUserCCQuery(undefined);
                setUserCCTotalQuery(undefined);
                setUserMainQueryTotal(undefined);
                props.setUserBenchmarkQuery && props.setUserBenchmarkQuery(undefined);
                props.setUserBenchmarkTotalQuery && props.setUserBenchmarkTotalQuery(undefined);
                // Start completely new query from page 0
                setPage(0);
                // Force trigger startNewQuery by setting newQuery to NEW
                props.setNewQuery('NEW');
              }}
            >
              <GoChevronLeft /> Start New Query
            </NavigationButton>
          )}

          {!saveDisabled && joinedData && !isDifferent && queryError === '' && (
            <DarkButton
              className="scaleHover"
              style={{ width: '150px', display: joinedData.length === 0 ? 'hidden' : 'block' }}
              type="button"
              onClick={handleSave}
            >
              Save
            </DarkButton>
          )}
          {isDifferent && (
            <DarkButton
              className="scaleHover"
              style={{ width: '150px' }}
              type="button"
              onClick={() => {
                setUserMainQuery(secondMainQuery);
                setUserCCQuery(secondCCQuery);
                setUserMainQueryTotal(mainQueryTotal);
                setUserCCTotalQuery(ccQueryTotal);
                // if (secondBenchmarkQuery) {
                props.setUserBenchmarkQuery(secondBenchmarkQuery);
                props.setUserBenchmarkTotalQuery(secondBenchmarkTotalQuery);
                // }
                if (element?.duplicated === true) {
                  updateTablePlaceholderDuplicate &&
                    updateTablePlaceholderDuplicate(mainQueryID, element.id, false, element.id);
                }
                // Reset isDifferent to hide APPLY button and show Save button
                setIsDifferent(false);
                // Update hideTotal ref to current value after APPLY
                initialHideTotalRef.current = hideTotal;
                props.setNewQuery('APPLY');
              }}
            >
              APPLY
            </DarkButton>
          )}
        </BottomRow>
      ) : (
        <BottomRow>
          {queryError !== '' ? (
            <BottomRow>
              <NavigationButton
                type="button"
                style={{ backgroundColor: 'transparent' }}
                onClick={() => {
                  // Reset queries completely
                  setUserMainQuery(undefined);
                  setUserCCQuery(undefined);
                  setUserCCTotalQuery(undefined);
                  setUserMainQueryTotal(undefined);
                  props.setUserBenchmarkQuery && props.setUserBenchmarkQuery(undefined);
                  props.setUserBenchmarkTotalQuery && props.setUserBenchmarkTotalQuery(undefined);
                  setQueryError && setQueryError('');
                  setPage(0);
                  // Force trigger startNewQuery
                  props.setNewQuery('NEW');
                }}
              >
                <GoChevronLeft /> Start New Query
              </NavigationButton>
            </BottomRow>
          ) : (
            <></>
          )}
        </BottomRow>
      )}
    </Preview>
  );
};

export default PreviewContainer;
